import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CreditCard, PaymentForm } from 'react-square-web-payments-sdk/dist/index.cjs';

import { InstanceProps } from '@flyblack/common/components/Modal';
import { SubmitError } from '@flyblack/common/components/Error';
import Card from '@flyblack/common/components/Card';
import Button from '@flyblack/common/components/Button';
import Icon from '@flyblack/common/components/Icon';
import Typography from '@flyblack/common/components/Typography';
import Spacer from '@flyblack/common/components/Spacer';

import { createSquareCardPaymentMethod } from '@/services/api/payment';
import SectionTitle from '@/components/SectionTitle';

import { SQUARE_APPLICATION_ID, SQUARE_LOCATION_ID } from '@/config';
import { translations } from '@/locale';
import { SessionContext } from '../Context/Session';
import Loading from '@flyblack/common/components/Loading';

interface Props extends InstanceProps {
  onSuccess: () => any;
}

const AddCreditCardModal = ({ close, onSuccess }: Props) => {
  const intl = useIntl();
  const { me, userCities } = React.useContext(SessionContext);

  const [isLoadingSquare, setIsLoadingSquare] = useState(false);
  const [cardSubmitError, setCardSubmitError] = useState(null);

  const submitCardInfo = ({ sourceId, verificationToken }) => {
    return createSquareCardPaymentMethod({ sourceId, verificationToken })
      .then((response) => {
        if (response.error) {
          setIsLoadingSquare(false);
          throw response.error;
        }

        close();
        onSuccess();
        setIsLoadingSquare(false);
      })
      .catch((error) => {
        setIsLoadingSquare(false);
        setCardSubmitError({
          message: intl.formatMessage({ id: translations.errors.api.credit_card_could_not_be_added })
        });
        throw { message: intl.formatMessage({ id: translations.errors.api.credit_card_could_not_be_added }) };
      });
  };

  const onPaymentError = (errors: any) => {
    setIsLoadingSquare(false);
  };

  return (
    <Card
      bg="white"
      className="rounded-none sm:rounded-md relative min-w-[320px] max-w-[542px] overflow-visible block text-black"
    >
      <Card.Row className="flex flex-row justify-end items-center">
        <Button type="button" appearance="white" className="z-[1] w-12 cursor-pointer" onClick={close}>
          <Icon type="close" className="text-flyblack-dark-gray" />
        </Button>
      </Card.Row>

      <Card.Row className="px-6 sm:px-14 md:px-24">
        <Card.Column padded={false} className="w-full">
          <Typography is="div" type="owl" className="pb-12">
            <FormattedMessage id={translations.modals.addCreditCardModal.title} />
          </Typography>

          <React.Fragment>
            <SectionTitle className="text-black pb-4">
              <FormattedMessage id={translations.modals.addCreditCardModal.cardInfo} />
            </SectionTitle>

            <Card.Row className="justify-between min-w-[320px] max-w-[542px]">
              <PaymentForm
                applicationId={SQUARE_APPLICATION_ID}
                locationId={SQUARE_LOCATION_ID}
                createVerificationDetails={() => {
                  setIsLoadingSquare(true);
                  return {
                    intent: 'STORE',
                    billingContact: {
                      givenName: me.firstName,
                      familyName: me.lastName,
                      city: userCities?.homeLocation?.name,
                      countryCode: userCities?.homeLocation?.countryCode
                    }
                  };
                }}
                cardTokenizeResponseReceived={(token, verifiedBuyer) => {
                  if (token.status === 'OK') {
                    // Success
                    submitCardInfo({
                      sourceId: token.token,
                      verificationToken: verifiedBuyer.token
                    });
                  } else {
                    // Failure
                    onPaymentError(token.errors);
                  }
                }}
              >
                <Loading visible={isLoadingSquare} center className="!absolute !left-[160px]">
                  <Loading.Indicator size={100} borderWidth={2} />
                </Loading>

                <CreditCard
                  render={(SquareButton) => (
                    <SquareButton
                      style={{
                        position: 'absolute',
                        padding: 0,
                        bottom: '0px',
                        right: '0px',
                        height: '56px',
                        zIndex: 1,
                        width: '152px',
                        borderRadius: '0',
                        backgroundColor: 'rgba(41, 41, 41, var(--tw-bg-opacity))'
                      }}
                    >
                      <FormattedMessage id={translations.inputs.buttons.addCard} />
                    </SquareButton>
                  )}
                />

                {!!cardSubmitError && <SubmitError error={cardSubmitError} />}

                <Spacer xs={3} />

                <Card.Row className="justify-between min-w-[320px] max-w-[542px]">
                  <Button appearance="lightGhost" fat className="w-[152px]" onClick={close}>
                    <FormattedMessage id={translations.inputs.buttons.cancel} />
                  </Button>
                  {/* Add Card button visually it's here, next to the cancel button */}
                </Card.Row>
              </PaymentForm>
            </Card.Row>

            <Spacer xs={6} />
          </React.Fragment>
        </Card.Column>
      </Card.Row>
    </Card>
  );
};

export default AddCreditCardModal;
